import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { SKU, SKUFormData, CURRENCY_OPTIONS } from '../../types/sku';
import { SKUModal } from './SKUModal';

interface SKUListProps {
  skus: SKU[];
  loading: boolean;
  totalSkus: number;
  activeSkus: number;
  inactiveSkus: number;
  onAdd: (data: SKUFormData) => Promise<void>;
  onUpdate: (skuId: string, data: Partial<SKUFormData>) => Promise<void>;
  onDelete: (skuId: string) => Promise<void>;
  onToggle: (skuId: string) => Promise<void>;
}

export const SKUList: React.FC<SKUListProps> = ({
  skus,
  loading,
  totalSkus,
  activeSkus,
  inactiveSkus,
  onAdd,
  onUpdate,
  onDelete,
  onToggle,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingSku, setEditingSku] = useState<SKU | null>(null);
  const [deletingSku, setDeletingSku] = useState<SKU | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const getCurrencySymbol = (code: string) => {
    const currency = CURRENCY_OPTIONS.find((c) => c.code === code);
    return currency?.symbol || code;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleToggle = async (sku: SKU) => {
    setActionLoading(sku._id);
    try {
      await onToggle(sku._id);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deletingSku) return;
    setActionLoading(deletingSku._id);
    try {
      await onDelete(deletingSku._id);
      setDeletingSku(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditSubmit = async (data: SKUFormData) => {
    if (!editingSku) return;
    await onUpdate(editingSku._id, data);
  };

  const existingSkuNames = skus.map((s) => s.sku);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            SKUs (Product Identifiers)
          </Typography>
          <Stack direction="row" spacing={1} mt={0.5}>
            <Chip
              label={`Total: ${totalSkus}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Active: ${activeSkus}`}
              size="small"
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Inactive: ${inactiveSkus}`}
              size="small"
              color="default"
              variant="outlined"
            />
          </Stack>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsAddModalOpen(true)}
          size="small"
        >
          Add SKU
        </Button>
      </Box>

      {/* SKU List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : skus.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'grey.50',
            borderStyle: 'dashed',
          }}
        >
          <Typography color="text.secondary">
            No SKUs configured. Click "Add SKU" to add your first product.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {skus.map((sku) => (
            <Paper
              key={sku._id}
              variant="outlined"
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                opacity: sku.isActive ? 1 : 0.7,
                backgroundColor: sku.isActive ? 'background.paper' : 'grey.50',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 1,
                },
              }}
            >
              {/* Toggle */}
              <Tooltip title={sku.isActive ? 'Deactivate' : 'Activate'}>
                <Switch
                  checked={sku.isActive}
                  onChange={() => handleToggle(sku)}
                  disabled={actionLoading === sku._id}
                  color="success"
                  size="small"
                />
              </Tooltip>

              {/* SKU Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontFamily: 'monospace',
                      color: sku.isActive ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {sku.sku}
                  </Typography>
                  <Chip
                    label={sku.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={sku.isActive ? 'success' : 'default'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 0.5,
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                  }}
                >
                  <Typography variant="caption">
                    {getCurrencySymbol(sku.currency)}{sku.price.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                    {sku.term}
                  </Typography>
                  {sku.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 200,
                      }}
                    >
                      {sku.description}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.disabled">
                  Added: {formatDate(sku.createdAt)}
                </Typography>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => setEditingSku(sku)}
                    disabled={actionLoading === sku._id}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeletingSku(sku)}
                    disabled={actionLoading === sku._id}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Add Modal */}
      <SKUModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={onAdd}
        existingSkuNames={existingSkuNames}
      />

      {/* Edit Modal */}
      <SKUModal
        open={!!editingSku}
        onClose={() => setEditingSku(null)}
        onSubmit={handleEditSubmit}
        editSku={editingSku}
        existingSkuNames={existingSkuNames}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletingSku}
        onClose={() => setDeletingSku(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete SKU</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingSku?.sku}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingSku(null)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={actionLoading === deletingSku?._id}
          >
            {actionLoading === deletingSku?._id ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
